import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowDownLeft, ArrowUpRight, Clock, Lock, Unlock } from "lucide-react";
import { useState } from "react";

import { Button } from "@/components/ui";
import { cn } from "@/lib/cn";
import { formatMoney, plural } from "@/lib/format";
import { localShiftRepository } from "@/repositories/localShiftRepository";
import { useAuthStore } from "@/stores/authStore";

import { CashMovementDialog } from "./CashMovementDialog";
import { CashUpDialog } from "./CashUpDialog";
import { OpenShiftDialog } from "./OpenShiftDialog";

/**
 * The strip above the till that says whose drawer this is.
 *
 * It answers one question at a glance — *is a shift open, and whose?* — because
 * the commonest cash-up dispute is not arithmetic, it is two people having used
 * one drawer without either of them opening a shift.
 *
 * Selling without a shift is **allowed**. Blocking it would be the tidier rule
 * and the wrong one: a customer is standing at the counter, and a shop that
 * cannot sell because of a bookkeeping step loses the sale, not the paperwork.
 * So the bar nags in amber instead, and the sale attaches to whatever shift is
 * open on that till when it uploads.
 */
export function ShiftBar({ tillId, tillName }: { tillId: string; tillName: string }) {
  const client = useQueryClient();
  const can = useAuthStore((s) => s.can);
  const session = useAuthStore((s) => s.session);

  const [opening, setOpening] = useState(false);
  const [cashingUp, setCashingUp] = useState(false);
  const [moving, setMoving] = useState<"PAY_IN" | "PAY_OUT" | null>(null);

  const current = useQuery({
    queryKey: ["shifts", "current", tillId],
    queryFn: () => localShiftRepository.current(tillId),
    enabled: Boolean(tillId),
    // A shift can be opened or closed on another terminal sharing this till.
    refetchInterval: 60_000,
  });

  const open = useMutation({
    mutationFn: (openingFloat: string) =>
      localShiftRepository.open({
        tillId,
        openingFloat,
        cashierName: session?.user?.displayName ?? "",
      }),
    onSuccess: () => {
      setOpening(false);
      void client.invalidateQueries({ queryKey: ["shifts"] });
    },
  });

  const shift = current.data?.shift ?? null;
  const offline = current.data?.queued ?? false;

  if (!tillId) return null;

  if (!shift || shift.status !== "OPEN") {
    return (
      <>
        <Strip tone="idle">
          <span className="flex items-center gap-2 text-sm text-ink-600">
            <Lock size={16} aria-hidden />
            {/* The old wording — "cash taken will not be counted against
                anybody" — stopped being true when the first sale started
                opening a shift itself. What is still true, and still worth
                doing something about, is that nobody will have counted the
                float. */}
            No shift open on {tillName || "this till"}. Selling will open one, but the float in
            the drawer will not have been counted.
          </span>
          {can("shifts.open") && (
            <Button size="sm" onClick={() => setOpening(true)}>
              Open shift
            </Button>
          )}
        </Strip>

        {opening && (
          <OpenShiftDialog
            tillName={tillName}
            pending={open.isPending}
            error={open.error}
            onClose={() => setOpening(false)}
            onOpen={(float) => open.mutate(float)}
          />
        )}
      </>
    );
  }

  const takings = shift.totals?.cash_sales;

  return (
    <>
      <Strip tone="open">
        <span className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
          <span className="flex items-center gap-2 font-medium text-ink-900">
            <Unlock size={16} className="text-brand-600" aria-hidden />
            {shift.cashier_name || "Shift"} on {shift.till_name || tillName}
          </span>
          <span className="flex items-center gap-1.5 text-ink-600">
            <Clock size={14} aria-hidden />
            since {timeOf(shift.opened_at)}
          </span>
          <span className="text-ink-600">
            {shift.opened_automatically ? (
              // Said plainly on the bar all day, because by cash-up it is too
              // late to count the float and the only fix is doing it tomorrow.
              <span className="text-warn-700">float never counted</span>
            ) : (
              <>float {formatMoney(shift.opening_float)}</>
            )}
            {takings !== undefined && <> · cash taken {formatMoney(takings)}</>}
            {shift.totals && <> · {plural(shift.totals.sale_count, "sale")}</>}
          </span>
          {offline && <span className="text-warn-700">not yet synced</span>}
        </span>

        <span className="flex flex-wrap gap-2">
          {can("cash.pay_in") && (
            <Button variant="ghost" size="sm" onClick={() => setMoving("PAY_IN")}>
              <ArrowDownLeft size={16} aria-hidden /> Cash in
            </Button>
          )}
          {can("cash.pay_out") && (
            <Button variant="ghost" size="sm" onClick={() => setMoving("PAY_OUT")}>
              <ArrowUpRight size={16} aria-hidden /> Cash out
            </Button>
          )}
          {can("shifts.close") && (
            <Button size="sm" onClick={() => setCashingUp(true)}>
              Cash up
            </Button>
          )}
        </span>
      </Strip>

      {moving && (
        <CashMovementDialog
          shiftId={shift.id}
          movementType={moving}
          onClose={() => setMoving(null)}
          onDone={() => {
            setMoving(null);
            void client.invalidateQueries({ queryKey: ["shifts"] });
          }}
        />
      )}

      {cashingUp && (
        <CashUpDialog
          shift={shift}
          onClose={() => setCashingUp(false)}
          onDone={() => {
            setCashingUp(false);
            void client.invalidateQueries({ queryKey: ["shifts"] });
            void client.invalidateQueries({ queryKey: ["sales"] });
          }}
        />
      )}
    </>
  );
}

function Strip({ tone, children }: { tone: "idle" | "open"; children: React.ReactNode }) {
  return (
    <div
      className={cn(
        "flex flex-wrap items-center justify-between gap-3 rounded-card border px-4 py-2.5",
        tone === "open" ? "border-line bg-surface" : "border-warn-500/30 bg-warn-50",
      )}
    >
      {children}
    </div>
  );
}

function timeOf(iso: string): string {
  return new Date(iso).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
}
